import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { useLanguage } from "@me8eon/language";
import { EonUiPagination } from "@eon-ui/eon-ui-components-react";
import OnboardingPortalLinkArrow from "../components/OnboardingPortalLinkArrow";
import OnboardingPortalTableHeaders from "../components/OnboardingPortalTableHeaders";
import OnboardingPortalStatusFilter from "../components/OnboardingPortalStatusFilter";
import OnboardingPortalOwnerFilter from "../components/OnboardingPortalOwnerFilter";
import OnboardingPortalExportButton from "../components/OnboardingPortalExportButton";
import OnboardingPortalSearchFilter from "../components/OnboardingPortalSearchFilter";
import OnboardingPortalLoader from "../components/OnboardingPortalLoader";
import OnboardingPortalRefreshButton from "../components/OnboardingPortalRefreshButton";
import {
    exportQnADataAsCsv,
    exportQnADataAsExcel,
} from "../OnboardingPortal.export.utils";

const ReviewQnAScreen = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const {
        showModule,
        reviewAllQnA,
        fetchReviewQnA,
        onReviewQnA,
        handleReviewQnA,
        onViewQnA,
        isLoadingReviewQnA,
        isSubmittingReviewQnA,
    } = useOnboardingPortalStore();
    const [lang] = useLanguage();
    const [owner, setOwner] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("pending");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const filteredQnA = useMemo(
        () =>
            reviewAllQnA.filter((qna) => {
                const matchesSearch =
                    qna.question.toLowerCase().includes(search.toLowerCase()) ||
                    qna.answer.toLowerCase().includes(search.toLowerCase());
                const matchesStatus =
                    status === "all" ? true : qna.status === status;
                const matchesOwner = owner === "" ? true : qna.owner === owner;
                return matchesSearch && matchesStatus && matchesOwner;
            }),
        [search, status, owner, reviewAllQnA]
    );

    useEffect(() => {
        const cp = currentPage;
        const ps = pageSize;
        setCurrentPage(1);
        if (cp > 1 && Math.ceil(filteredQnA.length / ps) < cp) {
            setCurrentPage(1);
        }
    }, [filteredQnA]);

    useEffect(() => {
        if (showModule === "review-qna") {
            fetchReviewQnA(jwt);
        }
    }, [showModule]);
    return (
        <>
            <div className="onboarding-portal--content--card">
                {(isLoadingReviewQnA || isSubmittingReviewQnA) && (
                    <OnboardingPortalLoader />
                )}
                <div className="onboarding-portal--content--card--header">
                    <div className="onboarding-portal--content--card--header--text">
                        {t("onboardingPortal.tableHeaders.qna")} (
                        {filteredQnA.length})
                    </div>
                    <div className="onboarding-portal--content--card--header--actions">
                        <OnboardingPortalRefreshButton
                            onClick={() => fetchReviewQnA(jwt)}
                        />
                        <OnboardingPortalSearchFilter
                            t={t}
                            search={search}
                            setSearch={setSearch}
                        />
                        <OnboardingPortalStatusFilter
                            t={t}
                            status={status}
                            setStatus={setStatus}
                        />
                        <OnboardingPortalOwnerFilter
                            t={t}
                            owner={owner}
                            setOwner={setOwner}
                            list={filteredQnA}
                        />
                        <OnboardingPortalExportButton
                            t={t}
                            onExportCSV={() =>
                                exportQnADataAsCsv(filteredQnA, "qna_export")
                            }
                            onExportExcel={() =>
                                exportQnADataAsExcel(filteredQnA, "qna_export")
                            }
                        />
                    </div>
                </div>
                <table className="onboarding-portal--table">
                    <OnboardingPortalTableHeaders
                        headers={[
                            "onboardingPortal.tableHeaders.question",
                            "onboardingPortal.tableHeaders.answer",
                            "onboardingPortal.tableHeaders.category",
                            "onboardingPortal.tableHeaders.status",
                            "onboardingPortal.tableHeaders.owner",
                            "onboardingPortal.tableHeaders.action",
                        ]}
                        t={t}
                    />
                    <tbody>
                        {filteredQnA
                            .slice(
                                (currentPage - 1) * pageSize,
                                currentPage * pageSize
                            )
                            .map((qna) => (
                                <tr key={qna.id}>
                                    <td>{qna.question}</td>
                                    <td>{qna.answer}</td>
                                    <td>{qna.category}</td>
                                    <td className={`status--${qna.status}`}>
                                        <span
                                            className={`status--${qna.status}`}
                                        >
                                            {t(
                                                `onboardingPortal.statusLabel.${qna.status}`
                                            )}
                                        </span>
                                    </td>
                                    <td>{qna.owner}</td>
                                    <td>
                                        <div className="actions-row">
                                            {qna.status === "pending" && (
                                                <>
                                                    <span
                                                        className="action--approve"
                                                        onClick={() => {
                                                            onReviewQnA(qna);
                                                        }}
                                                    >
                                                        {t(
                                                            "onboardingPortal.approveBtn"
                                                        )}
                                                    </span>
                                                    <span
                                                        className="action--reject"
                                                        onClick={() => {
                                                            onReviewQnA(qna);
                                                        }}
                                                    >
                                                        {t(
                                                            "onboardingPortal.rejectBtn"
                                                        )}
                                                    </span>
                                                    <OnboardingPortalLinkArrow
                                                        onClick={() => {
                                                            onReviewQnA(qna);
                                                        }}
                                                        noSpace={false}
                                                        size="small"
                                                    />
                                                </>
                                            )}
                                            {qna.status !== "pending" && (
                                                <span
                                                    className="action--view"
                                                    onClick={() =>
                                                        onViewQnA(qna)
                                                    }
                                                >
                                                    {t(
                                                        "onboardingPortal.viewBtn"
                                                    )}
                                                </span>
                                            )}
                                            {qna.status === "approved" && (
                                                <span
                                                    className="action--approve"
                                                    onClick={() => {
                                                        handleReviewQnA(
                                                            jwt,
                                                            qna.id || "",
                                                            "inQA",
                                                            "Moved to QA"
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "onboardingPortal.toQABtn"
                                                    )}
                                                </span>
                                            )}
                                            {qna.status === "inQA" && (
                                                <span
                                                    className="action--approve"
                                                    onClick={() => {
                                                        handleReviewQnA(
                                                            jwt,
                                                            qna.id || "",
                                                            "inProd",
                                                            "Moved to Production"
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "onboardingPortal.toProdBtn"
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <EonUiPagination
                lang={lang}
                begin={
                    filteredQnA.length === 0 ? 0 : (currentPage - 1) * pageSize
                }
                currentPage={currentPage}
                end={Math.min(currentPage * pageSize, filteredQnA.length)}
                initialPageSize={pageSize}
                pageSize={pageSize}
                pageSizes={"5,10,20,50,100"}
                totalItems={filteredQnA.length}
                onPageChanged={(e) => {
                    setCurrentPage(e.detail as any);
                    console.log(e.detail);
                }}
                onPageSizeChanged={(e) => {
                    setPageSize(e.detail);
                    setCurrentPage(1);
                }}
                onPageDecreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                }}
                onPageIncreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) =>
                        Math.min(
                            prev + 1,
                            Math.ceil(filteredQnA.length / pageSize)
                        )
                    );
                }}
            />
        </>
    );
};

export default ReviewQnAScreen;
